/**
 * Default object definitions and default attribute IDs for our floorplanner data model.
 *
 * Includes mapping of FilesAPI object IDs, attribute IDs, and utilities for quick lookup.
 *
 * - DefaultObject: the core "Room", "Wall", etc. objects (with fixed FilesAPI IDs)
 * - DefaultAttribute: essential attribute IDs for each object, with "forced" and "hidden" flags
 */

 import { AttributeModel, IAttribute } from '../../models/standard/AttributeModel';

 export enum ObjectType {
   Floor = 'Floor',
   Roof = 'Roof',
   Ceiling = 'Ceiling',
   Passage = 'Passage',
   Window = 'Window',
   Dormer = 'Dormer',
   Radiator = 'Radiator',
   Room = 'Room',
   Stair = 'Stair',
   Door = 'Door',
   Wall = 'Wall',
 }
 
 export interface DefaultObject {
   id: number;
   objectType: ObjectType;
 }
 
 // List of all default objects, with fixed IDs from the FilesAPI.
 export const DEFAULT_OBJECTS: DefaultObject[] = [
   { id: 1, objectType: ObjectType.Room },
   { id: 2, objectType: ObjectType.Wall },
   { id: 3, objectType: ObjectType.Window },
   { id: 4, objectType: ObjectType.Door },
   { id: 5, objectType: ObjectType.Radiator },
   { id: 6, objectType: ObjectType.Floor },
   { id: 7, objectType: ObjectType.Roof },
   { id: 8, objectType: ObjectType.Ceiling },
   { id: 9, objectType: ObjectType.Stair },
   { id: 10, objectType: ObjectType.Passage },
   { id: 11, objectType: ObjectType.Dormer },
 ];
 
 // Lookup: Object ID -> DefaultObject
 export const DEFAULT_OBJECT_MAP: Record<number, DefaultObject> = Object.fromEntries(DEFAULT_OBJECTS.map((obj) => [obj.id, obj]));
 
 // List of all default object IDs
 export const DEFAULT_OBJECT_IDS = DEFAULT_OBJECTS.map((obj) => obj.id) as number[];
 
 // Utility: Get the default object ID for a given object type
 export const getDefaultObjectId = (objectType: ObjectType): number => {
   const defaultObject = DEFAULT_OBJECTS.find((obj) => obj.objectType === objectType);
   if (!defaultObject) {
     throw new Error(`No default object found for object type: ${objectType}`);
   }
   return defaultObject.id;
 };
 
 export enum DefaultAttributeId {
   // Room
   ROOM_AREA = 7,
   ROOM_PERIMETER = 10,
   ROOM_NAME = 3,
   ROOM_ID = 2,
 
   // Wall
   WALL_THICKNESS = 28,
   WALL_TYPE = 24,
   WALL_ID = 22,
   WALL_LENGTH = 20,
   WALL_AREA = 12,
   GROSS_WALL_AREA = 23,
   NET_WALL_AREA = 26,
 
   // Window
   WINDOW_WIDTH = 32,
   WINDOW_ID = 31,
   WINDOW_AREA = 11,
 
   // Door
   DOOR_OPENING_DIRECTION = 41,
   DOOR_WIDTH = 38,
   DOOR_ID = 37,
   DOOR_AREA = 14,

   // Passage
   PASSAGE_WIDTH = 40,
 
   // Radiator
   RADIATOR_ID = 47,
   RADIATOR_DEPTH = 45,
   RADIATOR_LENGTH = 43,
   RADIATOR_AREA = 13,
 
   // Floor
   FLOOR_ID = 54,
   FLOOR_PERIMETER = 52,
   FLOOR_AREA = 50,
 }
 
 export interface DefaultAttribute {
   id: DefaultAttributeId;
   forced: boolean;
   hidden: boolean;
 }
 
 export const DEFAULT_ATTRIBUTES: Record<ObjectType, DefaultAttribute[]> = {
   [ObjectType.Room]: [
     { id: DefaultAttributeId.ROOM_AREA, forced: true, hidden: false },
     { id: DefaultAttributeId.ROOM_PERIMETER, forced: true, hidden: false },
     { id: DefaultAttributeId.ROOM_NAME, forced: false, hidden: true },
     { id: DefaultAttributeId.ROOM_ID, forced: false, hidden: true },
   ],
   [ObjectType.Wall]: [
     { id: DefaultAttributeId.WALL_THICKNESS, forced: false, hidden: true },
     { id: DefaultAttributeId.WALL_TYPE, forced: true, hidden: false },
     { id: DefaultAttributeId.WALL_ID, forced: false, hidden: true },
     { id: DefaultAttributeId.WALL_LENGTH, forced: false, hidden: true },
   ],
   [ObjectType.Window]: [
     { id: DefaultAttributeId.WINDOW_WIDTH, forced: false, hidden: true },
     { id: DefaultAttributeId.WINDOW_ID, forced: false, hidden: true },
   ],
   [ObjectType.Door]: [
     { id: DefaultAttributeId.DOOR_OPENING_DIRECTION, forced: true, hidden: false },
     { id: DefaultAttributeId.DOOR_WIDTH, forced: false, hidden: true },
     { id: DefaultAttributeId.DOOR_ID, forced: false, hidden: true },
   ],
   [ObjectType.Passage]: [
     { id: DefaultAttributeId.PASSAGE_WIDTH, forced: false, hidden: true },
   ],
   [ObjectType.Radiator]: [
     { id: DefaultAttributeId.RADIATOR_ID, forced: false, hidden: true },
     { id: DefaultAttributeId.RADIATOR_DEPTH, forced: false, hidden: true },
     { id: DefaultAttributeId.RADIATOR_LENGTH, forced: false, hidden: true },
   ],
   [ObjectType.Floor]: [
     { id: DefaultAttributeId.FLOOR_ID, forced: false, hidden: true },
     { id: DefaultAttributeId.FLOOR_PERIMETER, forced: true, hidden: false },
     { id: DefaultAttributeId.FLOOR_AREA, forced: true, hidden: false },
   ],
   [ObjectType.Roof]: [],
   [ObjectType.Ceiling]: [],
   [ObjectType.Stair]: [],
   [ObjectType.Dormer]: [],
 };
 
 export function getDefaultAttributesForObject(objectType: ObjectType): DefaultAttribute[] {
   return DEFAULT_ATTRIBUTES[objectType] || [];
 }
 
 export function getDefaultAttributeIdsForObject(objectType: ObjectType): DefaultAttributeId[] {
   return (DEFAULT_ATTRIBUTES[objectType] || []).map((attr) => attr.id);
 }
 
 export function getDefaultForcedAttributesForObject(objectType: ObjectType): DefaultAttribute[] {
   return (DEFAULT_ATTRIBUTES[objectType] || []).filter((attr) => attr.forced);
 }
 
 export function getDefaultHiddenAttributesForObject(objectType: ObjectType): DefaultAttribute[] {
   return (DEFAULT_ATTRIBUTES[objectType] || []).filter((attr) => attr.hidden);
 }
 
 /**
  * Updates the value of an attribute by ID in the attribute array.
  * If the attribute is found, its value is replaced with the new value.
  */
 export function syncAttributeValueById(attributes: AttributeModel[], attributeId: number, newValue: string): AttributeModel[] {
   return attributes.map((attr) =>
     attr.id === attributeId
       ? new AttributeModel(
           attr.id,
           attr.key,
           newValue,
           attr.predecessorId,
           attr.type,
           attr.unit,
           attr.hidden,
           attr.forced,
           // --- Pass extended properties to prevent data loss ---
           attr.ownerId,
           attr.userId,
           attr.publicFlag,
           attr.localization,
           attr.defaults,
           attr.validation,
           attr.unitFamily
         )
       : attr,
   );
 }
 
 export function syncIdAttributeByUuid(
   attributes: IAttribute[] | undefined,
   idAttrId: number,
   uuid: string
 ): IAttribute[] | undefined {
   if (!attributes) return attributes;
   return syncAttributeValueById(attributes as any, idAttrId, uuid);
 }
